import React from "react";
import { LayoutDashboard, ScanLine, Tags } from "lucide-react";

/**
 * Landing showcase cards. Real photographs (Unsplash, free for commercial use)
 * framed in our brutalist card system: 2px black border, hard drop shadow,
 * corner marks, and a header chip pulling in each feature's icon. We keep
 * the photo cropped tight (aspect-[4/3]) so all three cards stack the same
 * way regardless of source aspect ratio.
 */

const PHOTOS = {
  dashboard: {
    // Selective focus photography of soap party favors — handmade goods, neatly stocked.
    src: "https://images.unsplash.com/photo-1454873019514-eae2f086587a?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    alt: "Handmade products neatly arranged on a vendor table",
    label: "Inventory at a glance",
    chip: "DASHBOARD",
    icon: LayoutDashboard,
  },
  scanner: {
    // Black smartphone scanning a QR / barcode — fits the webcam scan-to-lookup feature.
    src: "https://images.unsplash.com/photo-1595079676601-f1adf5be5dee?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    alt: "Smartphone scanning a barcode",
    label: "Scan & sell from your phone",
    chip: "SCANNER",
    icon: ScanLine,
  },
  printTag: {
    // Wooden "Handmade" sign — strong tag/label metaphor with maker-friendly warmth.
    src: "https://images.unsplash.com/photo-1766933330352-81ff095ad04a?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    alt: "Wooden sign reading 'handmade'",
    label: "Print tags & barcodes",
    chip: "PRINT TAG",
    icon: Tags,
  },
};

function PhotoCard({ meta, testid }) {
  const Icon = meta.icon;
  return (
    <div className="brutal-card p-3 corner-marks" data-testid={testid}>
      <div className="flex items-center justify-between border-b-2 border-black pb-2 mb-3">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-[#C2410C]" />
          <span className="font-mono text-[10px] uppercase tracking-widest">{meta.chip}</span>
        </div>
        <span className="brutal-badge !py-0 !px-1 !text-[8px]">LIVE</span>
      </div>
      <div className="relative aspect-[4/3] overflow-hidden border-2 border-black bg-[#111]">
        <img
          src={meta.src}
          alt={meta.alt}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Top-left orange corner mark for brutalist flair */}
        <div className="absolute top-1 left-1 w-4 h-4 border-l-[3px] border-t-[3px] border-[#C2410C]" />
        <div className="absolute bottom-1 right-1 w-4 h-4 border-r-[3px] border-b-[3px] border-[#C2410C]" />
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest mt-3 text-[#4A4A4A]">
        {meta.label}
      </div>
    </div>
  );
}

export const DashboardShowcase = () => <PhotoCard meta={PHOTOS.dashboard} testid="showcase-dashboard-photo" />;
export const ScannerShowcase = () => <PhotoCard meta={PHOTOS.scanner} testid="showcase-scanner-photo" />;
export const PrintTagShowcase = () => <PhotoCard meta={PHOTOS.printTag} testid="showcase-print-photo" />;
